import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '@algoan/dashboard/store/store';
import { User, TeamConfig, DashboardRoles } from '@algoan/dashboard/model';
import { accessTokenTeamSelector, accessTokenOrganizationIdSelector } from './auth-selectors';

export const userSelector: (state: RootState) => User | undefined = createSelector(
  (state: RootState): User | undefined => state.user.user,
  accessTokenTeamSelector,
  (state: RootState): TeamConfig | undefined => state.appConfig?.config?.dashboard.teamConfig,
  (user, team, teamConfigInformation): User | undefined => {
    const teamInformation = teamConfigInformation?.teams.find((teamInformation) => team === teamInformation.name);

    return user
      ? {
          ...user,
          team: teamInformation,
          dashboardRole:
            user.dashboardRole === DashboardRoles.ADMIN && teamInformation
              ? DashboardRoles.MANAGER
              : user.dashboardRole,
        }
      : undefined;
  },
);

export const usersListSelector: (state: RootState) => User[] = createSelector(
  (state: RootState): User[] => state.user.usersList,
  (state: RootState): TeamConfig | undefined => state.appConfig?.config?.dashboard.teamConfig,
  (userList, teamConfigInformation): User[] => {
    return userList.map((user) => {
      return {
        ...user,
        team: teamConfigInformation?.teams.find((teamInformation) => user.teamName === teamInformation.name),
      };
    });
  },
);

export const userRoleSelector: (state: RootState) => {
  isAlgoanUser: boolean;
  isAdmin: boolean;
  isManager: boolean;
  isConsultant: boolean;
  isInitiator: boolean;
  isNone: boolean;
} = createSelector(
  userSelector,
  accessTokenOrganizationIdSelector,
  (
    user,
    organizationId,
  ): {
    isAlgoanUser: boolean;
    isAdmin: boolean;
    isManager: boolean;
    isConsultant: boolean;
    isInitiator: boolean;
    isNone: boolean;
  } => {
    const isAlgoanUser = organizationId === '*';
    const isAdmin = user?.dashboardRole === DashboardRoles.ADMIN;
    const isManager = user?.dashboardRole === DashboardRoles.MANAGER;
    const isConsultant = user?.dashboardRole === DashboardRoles.CONSULTANT;
    const isInitiator = user?.dashboardRole === DashboardRoles.INITIATOR;
    const isNone = user?.dashboardRole === DashboardRoles.NONE || user?.dashboardRole === undefined;

    return {
      isAlgoanUser: isAlgoanUser,
      isAdmin: isAdmin,
      isManager: isManager,
      isConsultant: isConsultant,
      isInitiator: isInitiator,
      isNone: isNone,
    };
  },
);
